import { useI18N } from '@susu/i18n/client'
import { Button, Loader } from '@susu/ui/components'
import { useMutation } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import { ScreenLayout } from '../../components/ScreenLayout'
import Avo from '../../../Avo/Avo'
import { flowProperties } from '../../lib/analytics'
import { useBookingStore } from '../../store'
import { submitPreAppointmentPurpose } from './api/pre-appointment-api'
import type { SelectedPurposeOption } from './PreAppointmentPurpose.types'
import {
  buildUpdateAppointmentPurposePayload,
  getActivePurposeOptions,
  hasPurposeInput,
} from './PreAppointmentPurpose.utils'

/**
 * Post-booking preferences: purpose chips + notes. Shown only when the host
 * did not pass a `serviceId` (options are prefetched on app init). Finish /
 * Skip lands on the final confirmation — matching the legacy Stencil screen.
 */
export const PreAppointmentPurpose = () => {
  const { t } = useI18N()
  const { bookingId, preAppointmentPurpose, setState } = useBookingStore()
  const [notes, setNotes] = useState('')
  const [selectedOption, setSelectedOption] =
    useState<SelectedPurposeOption | null>(null)

  const { category, options } = getActivePurposeOptions(preAppointmentPurpose)
  const hasInput = hasPurposeInput(selectedOption, notes)

  const submitMutation = useMutation({
    mutationFn: submitPreAppointmentPurpose,
    onSettled: () => {
      setState({
        screen: 'confirmation',
        screenHistory: [],
        confirmationVariant: 'preferences-saved',
        preferencesSkipped: false,
      })
    },
  })

  useEffect(() => {
    Avo.bookingToolStepViewed(flowProperties())
  }, [])

  const handleOptionClick = (option: SelectedPurposeOption) => {
    setSelectedOption((previous) =>
      previous?.value === option.value ? null : option,
    )
  }

  const handleSubmit = async () => {
    if (!hasInput) {
      Avo.preferencesSkipped({ ...flowProperties(), eventCategory: null })
      Avo.bookingToolStepCompleted(flowProperties())
      setState({
        screen: 'confirmation',
        screenHistory: [],
        confirmationVariant: 'booked',
        preferencesSkipped: true,
      })
      return
    }

    if (!bookingId) return

    Avo.preferencesSubmitted({
      ...flowProperties(),
      eventCategory: null,
      preferences: [
        {
          occasion: selectedOption?.value || '',
          // The plan requires `color`; this screen has no colour choice.
          color: '',
          note: Boolean(notes.trim()),
        },
      ],
    })
    Avo.bookingToolStepCompleted(flowProperties())

    try {
      await submitMutation.mutateAsync(
        buildUpdateAppointmentPurposePayload(bookingId, selectedOption, notes),
      )
    } catch {
      // onSettled already navigates to the thank-you confirmation (legacy
      // also proceeds on API failure so the user is never stuck here).
    }
  }

  if (!preAppointmentPurpose?.categories) {
    return (
      <ScreenLayout>
        <div className="flex-1 flex items-center justify-center">
          <Loader />
        </div>
      </ScreenLayout>
    )
  }

  return (
    <ScreenLayout>
      <div
        className="flex-1 flex flex-col justify-between overflow-y-auto"
        data-testid="pre-appointment-purpose"
      >
        <div className="w-full pt-12">
          <h1
            className="text-center text-[24px] leading-[32px] font-medium text-text mb-60 mt-0"
            data-testid="pre-appointment-purpose-heading"
          >
            {t('preAppointmentPurpose.title')}
          </h1>

          {category && options.length > 0 && (
            <div className="my-30">
              <h2
                className="text-sm leading-sm font-medium text-text ml-4 mb-16"
                data-testid="pre-appointment-purpose-category-heading"
              >
                {category.categoryDisplayName}
              </h2>
              <div className="flex flex-wrap gap-8">
                {options.map((option) => {
                  const selected = selectedOption?.value === option.value
                  return (
                    <button
                      key={option.value}
                      type="button"
                      className={`flex flex-col items-center gap-6 rounded-[6px] border px-24 py-16 text-sm leading-sm font-medium text-text cursor-pointer transition-[border-color] ${
                        selected
                          ? 'border-[#2D2E2C]'
                          : 'border-[#E1E1E1] hover:border-border-pressed'
                      }`}
                      onClick={() =>
                        handleOptionClick({
                          value: option.value,
                          waitwhileServiceId: option.waitwhileServiceId,
                          duration: option.duration,
                        })
                      }
                      aria-pressed={selected}
                      data-testid="pre-appointment-purpose-item-heading"
                    >
                      {option.displayName}
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          <div className="my-30">
            <h2
              className="text-sm leading-sm font-medium text-text ml-4 mb-16"
              data-testid="pre-appointment-purpose-category-heading"
            >
              {t('preAppointmentPurpose.anythingElse')}
            </h2>
            <textarea
              id="pre-appointment-notes"
              name="notes"
              className="block w-full h-[100px] resize-none rounded box-border border border-[#e1e1e1] bg-background px-10 py-10 text-base leading-base font-light text-text outline-none transition-[border-color] placeholder:text-text-placeholder hover:border-border-pressed focus:border-border-selected"
              placeholder={t('preAppointmentPurpose.notesPlaceholder')}
              value={notes}
              maxLength={500}
              onChange={(event) => setNotes(event.target.value)}
              data-testid="user-data-note"
            />
          </div>
        </div>

        <div className="sticky bottom-0 shrink-0 bg-surface py-16">
          <Button
            type="button"
            className="w-full"
            size="l"
            variant={hasInput ? 'filled' : 'outlined'}
            loading={submitMutation.isPending}
            onClick={handleSubmit}
            data-testid="appointment-purpose-submit-button"
          >
            {hasInput
              ? t('preAppointmentPurpose.finish')
              : t('preAppointmentPurpose.skip')}
          </Button>
        </div>
      </div>
    </ScreenLayout>
  )
}
